// @ts-nocheck
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { supabase } from '@/integrations/supabase/client';
import { Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { getSessionUser } from '@/lib/authSession';

interface TeamProspectFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const TeamProspectFormModal: React.FC<TeamProspectFormModalProps> = ({ 
  open, 
  onClose, 
  onSuccess 
}) => {
  const [formData, setFormData] = useState({
    team_name: '',
    school_organization: '',
    sport: '',
    competitive_level: '',
    coach_name: '',
    coach_email: '',
    coach_phone: '',
    team_size: '',
    age_group: '',
    geographic_region: '',
    pipeline_stage: 'target',
    priority_level: 'medium',
    lead_source: '',
    budget_range: '',
    preferred_contact_method: '',
    notes: ''
  });
  
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [loading, setLoading] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags(prev => [...prev, newTag.trim()]);
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(prev => prev.filter(tag => tag !== tagToRemove));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', (await getSessionUser()).data.user?.id)
        .single();

      if (!profile) {
        throw new Error('Profile not found');
      }

      const prospectData = {
        ...formData,
        sales_rep_id: profile.id,
        team_size: formData.team_size ? parseInt(formData.team_size) : null,
        tags
      };

      const { error } = await supabase
        .from('team_prospects')
        .insert([prospectData]);

      if (error) throw error;

      toast.success('Team prospect added successfully');
      onSuccess?.();
      onClose();
      
      // Reset form
      setFormData({
        team_name: '',
        school_organization: '',
        sport: '',
        competitive_level: '',
        coach_name: '',
        coach_email: '',
        coach_phone: '',
        team_size: '',
        age_group: '',
        geographic_region: '',
        pipeline_stage: 'target',
        priority_level: 'medium',
        lead_source: '',
        budget_range: '',
        preferred_contact_method: '',
        notes: ''
      });
      setTags([]);
    } catch (error) {
      console.error('Error adding prospect:', error);
      toast.error('Failed to add team prospect');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Team Prospect</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Basic Information</h3>
              
              <div>
                <Label htmlFor="team_name">Team Name *</Label>
                <Input
                  id="team_name"
                  value={formData.team_name}
                  onChange={(e) => handleInputChange('team_name', e.target.value)}
                  placeholder="e.g., Eagles Varsity Football"
                  required
                />
              </div>

              <div>
                <Label htmlFor="school_organization">School/Organization</Label>
                <Input
                  id="school_organization"
                  value={formData.school_organization}
                  onChange={(e) => handleInputChange('school_organization', e.target.value)}
                  placeholder="e.g., Lincoln High School"
                />
              </div>

              <div>
                <Label htmlFor="sport">Sport *</Label>
                <Select onValueChange={(value) => handleInputChange('sport', value)} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select sport" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="football">Football</SelectItem>
                    <SelectItem value="basketball">Basketball</SelectItem>
                    <SelectItem value="baseball">Baseball</SelectItem>
                    <SelectItem value="soccer">Soccer</SelectItem>
                    <SelectItem value="track_field">Track & Field</SelectItem>
                    <SelectItem value="wrestling">Wrestling</SelectItem>
                    <SelectItem value="volleyball">Volleyball</SelectItem>
                    <SelectItem value="tennis">Tennis</SelectItem>
                    <SelectItem value="cross_country">Cross Country</SelectItem>
                    <SelectItem value="swimming">Swimming</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="competitive_level">Competitive Level *</Label>
                <Select onValueChange={(value) => handleInputChange('competitive_level', value)} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high_school_varsity">High School Varsity</SelectItem>
                    <SelectItem value="high_school_jv">High School JV</SelectItem>
                    <SelectItem value="club_team">Club Team</SelectItem>
                    <SelectItem value="travel_team">Travel Team</SelectItem>
                    <SelectItem value="aau">AAU</SelectItem>
                    <SelectItem value="college_club">College Club</SelectItem>
                    <SelectItem value="adult_recreational">Adult Recreational</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Coach Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Coach Information</h3>
              
              <div>
                <Label htmlFor="coach_name">Coach Name *</Label>
                <Input
                  id="coach_name"
                  value={formData.coach_name}
                  onChange={(e) => handleInputChange('coach_name', e.target.value)}
                  placeholder="Coach full name"
                  required
                />
              </div>

              <div>
                <Label htmlFor="coach_email">Coach Email</Label>
                <Input
                  id="coach_email"
                  type="email"
                  value={formData.coach_email}
                  onChange={(e) => handleInputChange('coach_email', e.target.value)}
                  placeholder="coach@school.edu"
                />
              </div>

              <div>
                <Label htmlFor="coach_phone">Coach Phone</Label>
                <Input
                  id="coach_phone"
                  type="tel"
                  value={formData.coach_phone}
                  onChange={(e) => handleInputChange('coach_phone', e.target.value)}
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <Label htmlFor="geographic_region">Geographic Region</Label>
                <Input
                  id="geographic_region"
                  value={formData.geographic_region}
                  onChange={(e) => handleInputChange('geographic_region', e.target.value)}
                  placeholder="e.g., Northern California, Chicago Metro"
                />
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="team_size">Team Size</Label>
              <Input
                id="team_size"
                type="number"
                value={formData.team_size}
                onChange={(e) => handleInputChange('team_size', e.target.value)}
                placeholder="Number of players"
              />
            </div>

            <div>
              <Label htmlFor="priority_level">Priority Level</Label>
              <Select onValueChange={(value) => handleInputChange('priority_level', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Tags */}
          <div>
            <Label htmlFor="tags">Tags</Label>
            <div className="flex gap-2 mb-2">
              <Input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder="Add a tag"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
              />
              <Button type="button" variant="outline" onClick={addTag}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {tags.map((tag, index) => (
                <Badge key={index} variant="secondary" className="flex items-center gap-1">
                  {tag}
                  <X 
                    className="h-3 w-3 cursor-pointer" 
                    onClick={() => removeTag(tag)}
                  />
                </Badge>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Additional notes about this team prospect..."
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Adding...' : 'Add Prospect'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export { TeamProspectFormModal };